import React, {useState, useEffect, useMemo, useRef} from 'react';
import {Button, Table, Upload, message, Tag, Tabs, Modal, Select, Input, Tooltip, Switch, InputNumber} from 'antd';
import {UploadOutlined, DownloadOutlined, FileTextOutlined, InboxOutlined, EditOutlined, CheckOutlined, CloseOutlined, CloseCircleOutlined} from '@ant-design/icons';
import {useParams, useNavigate} from 'react-router-dom';
import {useQuery, useLazyQuery, useMutation} from '@apollo/client';
import {useTranslation} from 'react-i18next';
import type {ColumnsType} from 'antd/es/table';
import type {UploadFile} from 'antd';
import {GET_SORTER_PLAN_DETAIL} from './graphql/queries/getSorterPlanDetail';
import {DOWNLOAD_SORTER_PLAN_CHUTE_TEMPLATE} from './graphql/queries/downloadSorterPlanChuteTemplate';
import {IMPORT_SORTER_PLAN_CHUTES} from './graphql/mutations/importSorterPlanChutes';
import {EXPORT_SORTER_PLAN_CHUTES} from './graphql/mutations/exportSorterPlanChutes';
import {BATCH_SAVE_SORTER_PLAN_CHUTES} from './graphql/mutations/batchSaveSorterPlanChutes';
import {SAVE_SORTER_PLAN} from './graphql/mutations/saveSorterPlan';
import {GET_SORTER_PLAN_FLOW} from './graphql/queries/getSorterPlanFlow';
import {GET_TT_PRESORT_OPTIONS, GetTtPresortOptionsResponse, TtPresortOption} from './graphql/queries/getTtPresortOptions';
import {fileUpload} from '../../../utils/storageUtils';
import {gql} from '@apollo/client';
import {getLocalizedErrorMessage} from './types/SorterPlanChuteErrorMessage';
import {
    chuteTypeOptions,
    exceptionCodeOptions,
    UNASSIGNED_EXCEPTION_CODE,
    isExceptionChuteType,
    getChuteTypeName,
    getFinalMileCarrierName,
    CarrierOption,
    businessTypeOptions,
    getBusinessTypeName,
    printTypeOptions,
    getPrintTypeName,
    PrintType,
    BusinessType,
    ChuteType,
    TtBizType,
    ttBizTypeOptions,
    TEMU_ALLOWED_CARRIER_CODES,
    isFallbackChute
} from './types/ChuteEnums';
import {useFinalMileCarrierOptions} from './hooks/useFinalMileCarrierOptions';
import {useCarrierServiceMap} from '../../../hooks/useCarrierServiceMap';
import {useGetDefaultTtFromCode} from './hooks/useGetDefaultTtFromCode';
import {useWarehouseOptions} from '../../../hooks/useWarehouseOptions';
import FlowView from './components/FlowView';
import TemuSortingCodeSelect from './components/TemuSortingCodeSelect';
import {useTemuSortingCodeOptions} from './hooks/useTemuSortingCodeOptions';
import {
    LIST_ACTIVE_BAG_CONTAINERS,
    ListActiveBagContainersResponse,
    BagContainerOperateType,
} from '../bags/graphql/queries/listActiveBagContainers';

const {Dragger} = Upload;

interface ChuteConfigRecord {
    key?: string;
    id?: number;
    chuteCode?: string | null;
    leadChuteId?: number | null;
    matchesChuteIds?: string | null;
    chuteType: number | null;
    exceptionCodes?: string[] | null;
    carrierCode?: number | null;
    sortingCode?: string | null;
    businessType?: number | null;
    printType?: number | null;
    ttBizType?: string | null;
    preSortingCode?: string | null;
    lastMileSortingCode?: string | null;
    isUse?: boolean;
}

interface ImportFailedRecord {
    chuteCode: string;
    errorMessage: string;
    messageKey?: string;
    details?: string;
}

interface SorterPlanChuteRecord {
    id?: number;
    sorterPlanId?: number;
    chuteCode?: string;
    leadChuteId?: number;
    matchesChuteIds?: string;
    chuteType: number | null;
    exceptionCodes?: string[] | null;
    carrierCode?: number;
    sortingCode?: string;
    businessType?: number;
    printType?: number;
    ttBizType?: string | null;
    preSortingCode?: string | null;
    lastMileSortingCode?: string | null;
    createdAt?: string;
    createdBy?: string;
    updatedAt?: string;
    updatedBy?: string;
    isDeleted: boolean;
}

interface ImportResult {
    success: boolean;
    message: string;
    successCount: number;
    failedCount: number;
    failedRecords: ImportFailedRecord[];
    successRecords: SorterPlanChuteRecord[];
}


interface SortingCodeInputProps {
    initialValue: string;
    disabled: boolean;
    addonBefore?: string;
    // 第三段可为固定缩写字符串，也可为「尾程商服务」下拉（ReactNode）
    addonAfter?: React.ReactNode;
    placeholder: string;
    onCommit: (value: string) => void;
}

const SortingCodeInput: React.FC<SortingCodeInputProps> = React.memo(({
    initialValue,
    disabled,
    addonBefore,
    addonAfter,
    placeholder,
    onCommit,
}) => {
    // 分拣码/段码统一大写：已存的小写数据在页面上也展示为大写（与输入即转大写口径一致）
    const [localValue, setLocalValue] = useState(initialValue.toUpperCase());

    useEffect(() => {
        setLocalValue(initialValue.toUpperCase());
    }, [initialValue]);

    return (
        <Input
            value={localValue}
            placeholder={placeholder}
            disabled={disabled}
            addonBefore={addonBefore}
            addonAfter={addonAfter}
            // 分拣码/下一站等段码统一大写：小写查不到数据，输入即转大写
            onChange={(e) => setLocalValue(e.target.value.toUpperCase())}
            onBlur={() => onCommit(localValue)}
            onPressEnter={() => onCommit(localValue)}
        />
    );
});

const SorterPlanDetailPage: React.FC = () => {
    const {t, i18n} = useTranslation();
    const {id} = useParams<{id: string}>();
    const navigate = useNavigate();
    const [uploading, setUploading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [saving, setSaving] = useState(false);
    const [uploadModalVisible, setUploadModalVisible] = useState(false);
    const [importResult, setImportResult] = useState<ImportResult | null>(null);
    const [currentTab, setCurrentTab] = useState<string>('chutes');
    const [editableData, setEditableData] = useState<ChuteConfigRecord[]>([]);
    const [chuteCodeOptions, setChuteCodeOptions] = useState<string[]>([]);
    const [failedModalVisible, setFailedModalVisible] = useState(false);
    const [failedRecords, setFailedRecords] = useState<(ImportFailedRecord & { rowNumber: number })[]>([]);
    const [editingPlanName, setEditingPlanName] = useState(false);
    const [planNameValue, setPlanNameValue] = useState('');
    const [flowData, setFlowData] = useState<any[]>([]);
    const [isDropBagEnabled, setIsDropBagEnabled] = useState(false); // 落格组包开关状态
    const [printCount, setPrintCount] = useState(1); // 包牌打印数量，默认1
    const [ppmodBagContainerId, setPpmodBagContainerId] = useState<number | null>(null); // PPMode 容器类型，null-未配置
    const [defaultTtFromCode, setDefaultTtFromCode] = useState<string | null>(null); // TT 包牌始发地代码

    // 从后端获取尾程商选项
    const { options: finalMileCarrierOptions, loading: carrierOptionsLoading } = useFinalMileCarrierOptions(true);

    // 尾程商服务映射：carrierCode(FinalMileCarrier.value) → 服务列表（tt_location_config 子节点）
    const { serviceMap: carrierServiceMap } = useCarrierServiceMap();

    // 获取 TT 包牌始发地代码
    const { getDefaultTtFromCode } = useGetDefaultTtFromCode();

    // 预分拣候选缓存：key = `${fromCode}:${carrierCode}`，值为该 始发地×尾程商 的全量候选（数十行）。
    // 下一站/TT业务类型两维在渲染时本地过滤——查询只按尾程商粒度发一次，行数据就绪即预取，
    // 下拉展开时缓存已热，不再让用户对着加载态等（原实现按 下一站×业务类型 细粒度懒加载，展开才查）
    const [preSortOptionsCache, setPreSortOptionsCache] = useState<Map<string, TtPresortOption[]>>(new Map());
    const [fetchTtPresortOptions] = useLazyQuery<GetTtPresortOptionsResponse>(GET_TT_PRESORT_OPTIONS);
    // 预取在途去重：React state 更新是异步的，同一 carrier 的并发触发（effect + 多行下拉）会重复发请求
    const preSortFetchingRef = useRef<Set<string>>(new Set());

    const buildPreSortCacheKey = (carrierCode: number) => `${defaultTtFromCode}:${carrierCode}`;

    const prefetchPreSortOptions = async (carrierCode: number) => {
        // fromCode 未就绪不请求：否则后端回退"按尾程商全量"，跨仓候选混入且选中保存必被候选校验拒绝
        if (!defaultTtFromCode) return;
        const cacheKey = buildPreSortCacheKey(carrierCode);
        if (preSortOptionsCache.has(cacheKey) || preSortFetchingRef.current.has(cacheKey)) return;
        preSortFetchingRef.current.add(cacheKey);
        try {
            const { data, error } = await fetchTtPresortOptions({
                variables: { carrierCode, fromCode: defaultTtFromCode },
            });
            // 查询失败不写缓存，下次触发重试
            if (error || !data) return;
            setPreSortOptionsCache(prev => new Map(prev).set(cacheKey, data.getTtPresortOptions));
        } finally {
            preSortFetchingRef.current.delete(cacheKey);
        }
    };

    /**
     * 渲染时本地过滤候选，语义与后端 getTtPresortOptions 一致：只按下一站（分拣规则第二段）
     * 严格相等过滤，未填时无候选——配置表按下一站区分预分拣码，混入其它流向的码选中后
     * 保存/导入均能通过，但落格永不命中，配出静默死格口。
     *
     * 不按 TT业务类型过滤：后端已废弃该维度（候选码仅由 始发地+下一站+尾程商 决定，
     * 且按 presortCode 去重后单行携带的 ttBizType 取自任意一条重复行，不可作过滤依据）；
     * 业务类型合法性由保存侧后端候选校验兜底。
     */
    const filterPreSortCandidates = (carrierCode: number, nextStopCode: string): string[] => {
        if (!nextStopCode) return [];
        const all = preSortOptionsCache.get(buildPreSortCacheKey(carrierCode)) ?? [];
        return Array.from(new Set(
            all.filter(o => o.nextStopCode === nextStopCode).map(o => o.presortCode)
        ));
    };

    // 行数据就绪即预取：表中出现过的尾程商（TT 行）各查一次。尾程商是 Select 离散值、
    // 组合极少（≤尾程商枚举数），editableData 高频变化时靠 cache/在途去重免重复请求
    useEffect(() => {
        if (!defaultTtFromCode) return;
        const carriers = new Set(
            editableData
                .filter(item => item.businessType === BusinessType.TT && item.carrierCode != null)
                .map(item => item.carrierCode as number)
        );
        carriers.forEach(carrierCode => { prefetchPreSortOptions(carrierCode); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultTtFromCode, editableData]);

    // 将后端选项转换为前端使用的格式
    const carrierSelectOptions: CarrierOption[] = finalMileCarrierOptions.map(option => ({
        value: option.value,
        label: option.name,
        abbreviation: option.abbreviation
    }));

    // 辅助函数：将三段分拣码转换为四段显示格式
    // 例如: LAX-DW-A001 -> LAX-DW-A-001
    const parseSortingCodeForDisplay = (sortingCode: string | null | undefined): string => {
        if (!sortingCode || !sortingCode.trim()) {
            return '';
        }
        
        const trimmed = sortingCode.trim();
        const segments = trimmed.split('-');
        
        // 如果是三段格式，尝试将第三段拆分为字母和数字
        if (segments.length === 3) {
            const thirdSegment = segments[2];
            // 匹配格式：字母 + 数字（如 A001, B002）
            const match = thirdSegment.match(/^([A-Za-z]+)(\d+)$/);
            if (match) {
                // 拆分为四段：LAX-DW-A001 -> LAX-DW-A-001
                return `${segments[0]}-${segments[1]}-${match[1]}-${match[2]}`;
            }
        }
        
        // 其他格式保持不变
        return trimmed;
    };

    // 辅助函数：根据ID查找chuteCode（兼容字符串和数字类型）
    const getChuteCodeById = (id: number | string | null | undefined): string => {
        if (!id) return '';
        // 统一转换为数字进行比较
        const numericId = typeof id === 'string' ? parseInt(id) : id;
        const record = editableData.find(item => {
            const itemId = typeof item.id === 'string' ? parseInt(item.id as string) : item.id;
            return itemId === numericId;
        });
        return record?.chuteCode || '';
    };

    // 辅助函数：构建 TT 业务的分拣码
    // MIX 承运商(-1)：两段格式 (第一段-第二段)
    // 其他承运商：三段格式 (第一段-第二段-第三段)
    // thirdSegment：显式指定第三段（如尾程商服务码 USMPM）；不传或为空时回退用尾程商缩写（=「不区分」）
    const buildTtSortingCode = (
        middleSegment: string | null,
        carrierCode: number | null | undefined,
        thirdSegment?: string | null
    ): string => {
        if (!defaultTtFromCode || !middleSegment || carrierCode === null || carrierCode === undefined) {
            return middleSegment || '';
        }

        // MIX 承运商：两段格式（不需要第三段）
        if (carrierCode === -1) {
            return `${defaultTtFromCode}-${middleSegment}`;
        }

        // 其他承运商：三段格式；有显式第三段用之，否则用尾程商缩写
        const third = thirdSegment && thirdSegment.length > 0
            ? thirdSegment
            : getFinalMileCarrierName(carrierCode, carrierSelectOptions, true);

        return `${defaultTtFromCode}-${middleSegment}-${third}`;
    };

    // 辅助函数：从完整分拣码中提取第二段（用户输入部分）
    const extractMiddleSegment = (fullSortingCode: string | null | undefined): string => {
        if (!fullSortingCode) return '';
        
        const segments = fullSortingCode.split('-');
        
        // 三段格式：返回第二段
        if (segments.length === 3) {
            return segments[1];
        }
        
        // 两段格式（MIX 承运商）：中段定义上就是第二段，与首段是否等于本仓无关。
        // 不可按首段是否匹配 defaultTtFromCode 分支——异仓/旧映射首段（如 EWR1-ATL1 而本仓 LAX1）
        // 会取到 EWR1 并被重拼成 LAX1-EWR1，导致下一站段丢失。
        if (segments.length === 2) {
            return segments[1];
        }
        
        // 单段格式，返回原值
        return fullSortingCode;
    };

    // 辅助函数：从三段分拣码中提取第三段（尾程商缩写 或 尾程商服务码）；非三段返回空串
    const extractThirdSegment = (fullSortingCode: string | null | undefined): string => {
        if (!fullSortingCode) return '';
        const segments = fullSortingCode.split('-');
        return segments.length === 3 ? segments[2] : '';
    };

    // 辅助函数：判断是否需要使用 TT 分拣规则特殊格式（带前后缀，仅编辑中段）
    // 条件：包牌码类型=TT（TT+SWX 样式的分拣规则同样是三段式，与包牌样式无关）
    const shouldUseTtThreeSegmentCode = (businessType: number | null | undefined, carrierCode: number | null | undefined, _printType?: number | null): boolean => {
        return businessType === BusinessType.TT && carrierCode !== null && carrierCode !== undefined;
    };

    // 辅助函数：分拣规则第一、二段是否相同（TT+SWX 包牌样式的前置条件，如 LAX1-LAX1-SWX）
    const isLocalSwxSortingRule = (sortingCode: string | null | undefined): boolean => {
        if (!sortingCode) return false;
        const segments = sortingCode.split('-');
        return segments.length >= 2 && segments[0] !== '' && segments[0] === segments[1];
    };

    // 处理流向数据，构建树形结构
    const processFlowData = (rawData: any[]) => {
        const result: any[] = [];

        // 按 chuteType 分组（格口类型两类化：异常统一一组；旧非业务异常(2)已刷数归并，防御性并入异常组）
        const normalChutes: any[] = [];
        const exceptionChutes: any[] = [];
        const unusedChutes: any[] = [];

        rawData.forEach(item => {
            if (item.chuteType === 0) {
                normalChutes.push(item);
            } else if (item.chuteType === 1 || item.chuteType === 2) {
                exceptionChutes.push(item);
            } else if (item.chuteType === null) {
                unusedChutes.push(item);
            }
        });
        
        // 层级定义：按方案列顺序分组（产品确认）：
        // 包牌码类型 → TT业务类型 → 尾程商 → 分拣规则 → 预分拣码 → 包牌样式 → 尾程业务段码
        const groupLevels: { key: string; name: string }[] = [
            { key: 'businessType', name: 'businessTypeName' },
            { key: 'ttBizType', name: 'ttBizTypeName' },
            { key: 'carrierCode', name: 'carrierName' },
            { key: 'sortingCode', name: 'sortingCode' },
            { key: 'preSortingCode', name: 'preSortingCode' },
            { key: 'printType', name: 'printTypeName' },
            { key: 'lastMileSortingCode', name: 'lastMileSortingCode' },
        ];

        // 递归分组构建树：
        // - 单条分组直接作为叶子（保留完整字段展示）
        // - 本层所有条目取值相同或为空时不建中间节点，直接下钻（避免单链节点）
        const buildFlowTree = (items: any[], levelIndex: number, keyPrefix: string): any[] => {
            if (levelIndex >= groupLevels.length) {
                return items.map((item, idx) => ({
                    ...item,
                    key: `${keyPrefix}-chute-${item.chuteNumbers || idx}`,
                    validChuteCount: 1
                }));
            }
            const level = groupLevels[levelIndex];
            const groups = new Map<string, any[]>();
            items.forEach(item => {
                const value = item[level.key];
                const groupKey = value != null && value !== '' ? String(value) : 'null';
                if (!groups.has(groupKey)) groups.set(groupKey, []);
                groups.get(groupKey)!.push(item);
            });

            if (groups.size === 1) {
                const onlyKey = Array.from(groups.keys())[0];
                if (items.length === 1) {
                    return [{ ...items[0], key: `${keyPrefix}-${level.key}-${onlyKey}`, validChuteCount: 1 }];
                }
                return buildFlowTree(items, levelIndex + 1, `${keyPrefix}-${level.key}-${onlyKey}`);
            }

            const nodes: any[] = [];
            groups.forEach((groupItems, groupKey) => {
                const nodeKey = `${keyPrefix}-${level.key}-${groupKey}`;
                if (groupItems.length === 1) {
                    nodes.push({ ...groupItems[0], key: nodeKey, validChuteCount: 1 });
                    return;
                }
                if (groupKey === 'null') {
                    nodes.push(...buildFlowTree(groupItems, levelIndex + 1, nodeKey));
                    return;
                }
                nodes.push({
                    key: nodeKey,
                    [level.key]: groupItems[0][level.key],
                    [level.name]: groupItems[0][level.name],
                    validChuteCount: groupItems.length,
                    physicalChuteCount: groupItems.reduce((sum: number, item: any) => sum + item.physicalChuteCount, 0),
                    chuteNumbers: groupItems.map((i: any) => i.chuteNumbers).filter((s: string) => s).sort().join(', '),
                    chuteType: 0,
                    children: buildFlowTree(groupItems, levelIndex + 1, nodeKey)
                });
            });
            return nodes;
        };

        result.push(...buildFlowTree(normalChutes, 0, 'flow'));
        
        // 处理异常格口：后端每格口一行（带各自异常类型），单行直出、多行构建父子
        if (exceptionChutes.length > 0) {
            if (exceptionChutes.length === 1) {
                result.push({
                    key: `exception-single`,
                    ...exceptionChutes[0]
                });
            } else {
                const children = exceptionChutes.map((item: any, i: number) => ({
                    ...item,
                    key: `exception-chute-${i}`
                }));
                result.push({
                    key: 'exception',
                    carrierCode: null,
                    carrierName: null,
                    sortingCode: null,
                    validChuteCount: exceptionChutes.length,
                    physicalChuteCount: exceptionChutes.reduce((sum: number, item: any) => sum + (item.physicalChuteCount || 0), 0),
                    chuteNumbers: exceptionChutes.map((item: any) => item.chuteNumbers).filter((sVal: string) => sVal).sort().join(', '),
                    chuteType: 1,
                    children
                });
            }
        }

        // 处理未使用格口（chuteType = null）
        if (unusedChutes.length > 0) {
            // 未使用格口汇总显示，不展开
            const item = unusedChutes[0]; // 后端返回聚合数据，只有一条记录

            result.push({
                key: 'unused',
                carrierCode: null,
                carrierName: null,
                sortingCode: null,
                validChuteCount: item.validChuteCount,
                physicalChuteCount: item.physicalChuteCount,
                chuteNumbers: item.chuteNumbers || '',
                chuteType: null
            });
        }
        return result;
    };

    // 辅助函数：根据chuteCode查找ID
    const getIdByChuteCode = (chuteCode: string): number | null => {
        if (!chuteCode) return null;
        const record = editableData.find(item => item.chuteCode === chuteCode);
        return record?.id || null;
    };

    // 辅助函数：解析 matchesChuteIds JSON 数组为 chuteCode 数组
    const parseMatchesChuteIds = (matchesChuteIds: string | null | undefined): string[] => {
        if (!matchesChuteIds) return [];
        try {
            // 解析 JSON，可能是数字数组或字符串数组
            const ids: (number | string)[] = JSON.parse(matchesChuteIds);
            return ids.map(id => getChuteCodeById(id)).filter(code => code);
        } catch (e) {
            console.error('解析 matchesChuteIds 失败:', e, matchesChuteIds);
            return [];
        }
    };

    // 辅助函数：将 chuteCode 数组转换为 matchesChuteIds JSON 字符串
    const formatMatchesChuteIds = (chuteCodes: string[]): string | null => {
        const ids = chuteCodes.map(code => getIdByChuteCode(code)).filter(id => id !== null) as number[];
        return ids.length > 0 ? JSON.stringify(ids) : null;
    };

    // 验证函数：检查配对格口号是否连续
    const validateMatchedChutes = (record: ChuteConfigRecord): string | null => {
        if (!record.matchesChuteIds) {
            return null;
        }

        try {
            // 解析配对格口号
            const matchedChuteCodes = parseMatchesChuteIds(record.matchesChuteIds);
            if (matchedChuteCodes.length <= 1) {
                return null; // 只有一个或没有格口，不需要验证
            }

            // 提取前缀和数字
            const chutesWithNumbers = matchedChuteCodes.map(code => {
                const prefix = code.match(/^[A-Za-z]+/)?.[0] || '';
                const number = parseInt(code.replace(/^[A-Za-z]+/, '') || '0');
                return { code, prefix, number };
            });

            // 检查是否都是同一前缀
            const firstPrefix = chutesWithNumbers[0].prefix;
            const allSamePrefix = chutesWithNumbers.every(item => item.prefix === firstPrefix);
            if (!allSamePrefix) {
                return '配对格口号必须是同一类型';
            }

            // 按数字排序
            const sortedChutes = chutesWithNumbers.sort((a, b) => a.number - b.number);

            // 检查是否连续
            for (let i = 0; i < sortedChutes.length - 1; i++) {
                if (sortedChutes[i + 1].number - sortedChutes[i].number !== 1) {
                    return `配对格口号不连续（${sortedChutes[i].code} 和 ${sortedChutes[i + 1].code} 之间有间隔）`;
                }
            }

            return null; // 验证通过
        } catch (e) {
            return null; // 解析失败，不显示错误
        }
    };

    // 查询方案详情
    const {data, loading, refetch} = useQuery(GET_SORTER_PLAN_DETAIL, {
        variables: {id: id ? parseInt(id) : 0},
        skip: !id,
        onCompleted: (data) => {
            // 初始化落格组包状态
            if (data?.getSorterPlanDetail?.isDropBag !== undefined) {
                setIsDropBagEnabled(data.getSorterPlanDetail.isDropBag);
            }

            // 初始化包牌打印数量
            if (data?.getSorterPlanDetail?.printCount !== undefined && data?.getSorterPlanDetail?.printCount !== null) {
                setPrintCount(data.getSorterPlanDetail.printCount);
            }

            // 初始化 PPMode 容器类型
            setPpmodBagContainerId(data?.getSorterPlanDetail?.ppmodBagContainerId ?? null);

            // 初始化可编辑数据（后端返回三段格式，前端转换为四段显示）
            if (data?.getSorterPlanDetail?.chuteConfigs) {
                let initialData = data.getSorterPlanDetail.chuteConfigs.map((config: any, index: number) => ({
                    key: `config-${index}`,
                    id: config.id,
                    chuteCode: config.chuteCode || '',
                    leadChuteId: config.leadChuteId,
                    matchesChuteIds: config.matchesChuteIds || null,
                    chuteType: config.chuteType ?? null,
                    exceptionCodes: isExceptionChuteType(config.chuteType) ? (config.exceptionCodes ?? null) : null,
                    carrierCode: config.carrierCode,
                    sortingCode: config.sortingCode || null,
                    businessType: config.businessType,
                    printType: config.printType ?? PrintType.SWIFTX,
                    ttBizType: config.ttBizType ?? null,
                    preSortingCode: config.preSortingCode ?? null,
                    lastMileSortingCode: parseSortingCodeForDisplay(config.lastMileSortingCode),
                    isUse: config.isUse || false
                }));
                
                // 重新计算并同步所有的 matchesChuteIds，确保数据一致性
                const leadChuteMap = new Map<number, Set<number>>();
                
                // 构建主格口映射（leadChuteId -> Set of chute IDs）
                initialData.forEach((chute: ChuteConfigRecord) => {
                    if (chute.leadChuteId && chute.id) {
                        if (!leadChuteMap.has(chute.leadChuteId)) {
                            leadChuteMap.set(chute.leadChuteId, new Set([chute.leadChuteId]));
                        }
                        leadChuteMap.get(chute.leadChuteId)!.add(chute.id);
                    }
                });
                
                // 为每个格口重新设置正确的 matchesChuteIds
                initialData = initialData.map((chute: ChuteConfigRecord) => {
                    if (!chute.leadChuteId) {
                        return {
                            ...chute,
                            matchesChuteIds: null
                        };
                    }

                    if (leadChuteMap.has(chute.leadChuteId)) {
                        const matchedIds = Array.from(leadChuteMap.get(chute.leadChuteId)!).sort((a, b) => a - b);
                        const matchesChuteIds = JSON.stringify(matchedIds);
                        return {
                            ...chute,
                            matchesChuteIds
                        };
                    }
                    
                    return chute;
                });
                
                setEditableData(initialData);
            }
        }
    });

    const planDetail = data?.getSorterPlanDetail;

    // 作业仓名称回显（只读）
    const {warehouseOptions: warehouseNameOptions} = useWarehouseOptions('ALL');
    const workWarehouseName = useMemo(() => {
        if (!planDetail?.warehouseId) return undefined;
        return warehouseNameOptions.find(w => w.id === String(planDetail.warehouseId))?.name;
    }, [warehouseNameOptions, planDetail?.warehouseId]);

    // 初始化方案名称编辑值
    useEffect(() => {
        if (planDetail?.sorterPlanName) {
            setPlanNameValue(planDetail.sorterPlanName);
        }
    }, [planDetail?.sorterPlanName]);

    // 获取 TT 包牌始发地代码
    useEffect(() => {
        const fetchDefaultTtFromCode = async () => {
            if (planDetail?.warehouseId) {
                const fromCode = await getDefaultTtFromCode(planDetail.warehouseId);
                setDefaultTtFromCode(fromCode);
            }
        };
        fetchDefaultTtFromCode();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [planDetail?.warehouseId]);

    // 更新方案名称
    const [updateSorterPlan] = useMutation(SAVE_SORTER_PLAN, {
        onCompleted: () => {
            message.success(t('wms.sorterPlan.messages.updateSuccess'));
            setEditingPlanName(false);
            refetch();
        },
        onError: (error) => {
            console.error('Update sorter plan error:', error);
            message.error(t('wms.sorterPlan.messages.updateFailed'));
        }
    });

    // 新建方案时，初始化所有格口为主格口
    useEffect(() => {
        if (!id && chuteCodeOptions.length > 0 && editableData.length === 0) {
            const initialData = chuteCodeOptions.map((chuteCode, index) => ({
                key: `chute-${index}`,
                id: undefined, // 新建时没有id
                chuteCode,
                leadChuteId: undefined, // 默认为主格口（leadChuteId为空）
                matchesChuteIds: null,
                chuteType: null,
                carrierCode: undefined,
                sortingCode: '',
                businessType: undefined,
                printType: PrintType.SWIFTX,
                ttBizType: null,
                preSortingCode: null,
                lastMileSortingCode: ''
            }));
            setEditableData(initialData);
        }
    }, [id, chuteCodeOptions, editableData.length]);

    // 下载模板
    // 获取流向统计数据
    const {data: flowDataResult, loading: flowLoading, refetch: refetchFlow} = useQuery(GET_SORTER_PLAN_FLOW, {
        variables: {sorterPlanId: id},
        skip: !id || currentTab !== 'flow',
        onCompleted: (data) => {
            if (data?.getSorterPlanFlow?.success && data?.getSorterPlanFlow?.data) {
                // 处理流向数据，构建树形结构
                const processedData = processFlowData(data.getSorterPlanFlow.data);
                setFlowData(processedData);
            }
        }
    });

    const [downloadTemplate, {loading: downloadingTemplate}] = useLazyQuery(
        DOWNLOAD_SORTER_PLAN_CHUTE_TEMPLATE,
        {
            fetchPolicy: 'network-only',
            onCompleted: (data) => {
                try {
                    const base64Data = data.downloadSorterPlanChuteTemplate;
                    const byteCharacters = atob(base64Data);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    const blob = new Blob([byteArray], {
                        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                    });

                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `sorter_plan_chute_template_${i18n.language}.xlsx`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);

                    message.success(t('wms.sorterPlan.detail.messages.downloadTemplateSuccess'));
                } catch (error) {
                    console.error('Download template error:', error);
                    message.error(t('wms.sorterPlan.detail.messages.downloadTemplateFailed'));
                }
            },
            onError: (error) => {
                console.error('Download template error:', error);
                message.error(t('wms.sorterPlan.detail.messages.downloadTemplateFailed'));
            }
        }
    );

    // 导入格口
    const [importChutes] = useMutation(IMPORT_SORTER_PLAN_CHUTES);

    // 导出格口
    const [exportChutes] = useMutation(EXPORT_SORTER_PLAN_CHUTES);

    // 批量保存格口
    const [batchSaveChutes] = useMutation(BATCH_SAVE_SORTER_PLAN_CHUTES);

    // 解析边角格口配置列表
    const disabledChuteCodes = React.useMemo(() => {
        if (!planDetail?.disabledChuteConfig) return [];
        return planDetail.disabledChuteConfig
            .split(',')
            .map((s: string) => s.trim())
            .filter((s: string) => s);
    }, [planDetail?.disabledChuteConfig]);

    const editableDataMap = useMemo(() => {
        const map = new Map<string, ChuteConfigRecord>();
        editableData.forEach(item => { if (item.key) map.set(item.key, item); });
        return map;
    }, [editableData]);

    // 有 TEMU 行才拉分段候选（目的地 / 大箱码），页面级一次供所有 TEMU 行共用
    const hasTemuRows = useMemo(() => editableData.some(item => item.businessType === BusinessType.TEMU), [editableData]);
    const temuSortingCodeOptions = useTemuSortingCodeOptions(hasTemuRows);

    // PPMode 容器类型：仅作业仓为分拣仓且方案里有 TEMU 格口时展示（后端也只对分拣仓 Temu 单限重）；
    // 候选与 PDA PPMode 装包同一份 TEMU 容器字典，不展示时不请求
    const showPpmodContainer = !!data?.getSorterPlanDetail?.isSortingHub && hasTemuRows;
    const {data: containerData} = useQuery<ListActiveBagContainersResponse>(LIST_ACTIVE_BAG_CONTAINERS, {
        variables: {operateType: BagContainerOperateType.TEMU},
        skip: !showPpmodContainer,
    });
    const ppmodContainerOptions = useMemo(
        () => (containerData?.listActiveBagContainers ?? []).map(c => ({label: c.name, value: c.id})),
        [containerData],
    );

    // 异常格口行：异常类型单元格横向合并吃掉后续 7 列（包牌码类型…尾程业务段码），
    // 让异常类型平铺占满整行、堆到页宽自动换行；那 7 个维度对异常格口本就不适用（render 里也都置灰为 --）
    const EXCEPTION_ROW_COL_SPAN = 8;
    /** 被合并 8 列（异常类型 80 + 包牌码类型 140 + TT业务类型 110 + 尾程商 180 + 分拣规则 260 + 包牌样式 110 + 预分拣码 150 + 尾程业务段码 220）的宽度之和 */
    const EXCEPTION_ROW_MAX_WIDTH = 1250;
    const isExceptionRow = (record: ChuteConfigRecord): boolean =>
        isExceptionChuteType(editableDataMap.get(record.key!)?.chuteType ?? null);
    // 被合并吃掉的列返回 colSpan 0（antd 据此不渲染该 td）
    const mergedAwayCell = (record: ChuteConfigRecord) =>
        isExceptionRow(record) ? { colSpan: 0 } : {};

    // 检查格口是否正在使用中（不包括边角格口检查，边角格口可以编辑）
    const isChuteDisabled = (chuteCode?: string | null, isUse?: boolean): boolean => {
        if (!chuteCode) return false;

        // 直线分拣机的 000 兜底格口：语义固定为「异常 + 未分配」，整行只读。
        // 放在这里而不是逐列加判断——本页 12 个可编辑列的 disabled 都走这个函数，
        // 改一处即可整行置灰，也不会漏掉将来新增的列。
        if (isFallbackChute(planDetail?.fallbackChuteConfig, chuteCode)) return true;

        // 检查格口是否正在使用中
        // 如果格口正在使用中（isUse === true），则禁用编辑
        if (isUse !== null && isUse !== undefined) return isUse;
        
        // 默认不禁用
        return false;
    };

    // 表格列定义
    const columns: ColumnsType<ChuteConfigRecord> = [
        {
            title: t('wms.sorterPlan.detail.columns.chuteNumber'),
            dataIndex: 'chuteCode',
            key: 'chuteCode',
            width: 120,
            render: (text: string, record: ChuteConfigRecord) => {
                return (
                    <Input
                        value={text || ''}
                        placeholder="格口号"
                        disabled={true}
                        style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                    />
                );
            }
        },
        {
            title: t('wms.sorterPlan.detail.columns.isChuteConfigured'),
            dataIndex: 'leadChuteId',
            key: 'leadChuteId',
            width: 140,
            render: (leadChuteId: number | null, record: ChuteConfigRecord) => {
                const currentRecord = editableDataMap.get(record.key!);
                const currentIndex = editableData.findIndex(item => item.key === record.key);
                
                // 判断当前格口是否是主格口
                const isLeadChute = !currentRecord?.leadChuteId || currentRecord.leadChuteId === currentRecord.id;
                let isRestricted = false;
                
                // 判断当前格口是否在边角格口配置中
                // 如果在边角格口配置中，则该格口只能作为主格口，不能向上配对
                if (currentRecord?.chuteCode && disabledChuteCodes.includes(currentRecord.chuteCode)) {
                    isRestricted = true;
                } else {
                    // 如果不在边角格口配置中，则判断是否是格口组的第一个格口（前面没有同组的格口）
                    const currentPrefix = currentRecord?.chuteCode?.match(/^[A-Za-z]+/)?.[0] || '';
                    // 向前查找是否有相同前缀的格口
                    const hasSamePrefixBefore = editableData.slice(0, currentIndex).some(item => {
                        const itemPrefix = item.chuteCode?.match(/^[A-Za-z]+/)?.[0] || '';
                        return itemPrefix === currentPrefix;
                    });
                    // 如果前面没有同组格口，则不能选择向上配对
                    if (!hasSamePrefixBefore) {
                        isRestricted = true;
                    }
                }
                
                // 检查是否正在使用中（使用 isUse 判断）
                const isDisabled = isChuteDisabled(record.chuteCode, currentRecord?.isUse);
                
                return (
                    <Select
                        value={isLeadChute ? 'lead' : 'pair'}
                        style={{ width: '100%' }}
                        disabled={isRestricted || isDisabled}
                        onChange={(value: 'lead' | 'pair') => {
                            if (value === 'lead') {
                                // 改为主格口
                                handleChangeToLeadChute(currentIndex);
                            } else {
                                // 改为向上配对
                                handleChangeToPairUpward(currentIndex);
                            }
                        }}
                        options={[
                            { value: 'lead', label: t('wms.sorterPlan.detail.columns.isLeadChute') },
                            { value: 'pair', label: t('wms.sorterPlan.detail.columns.pairUpward'), disabled: isRestricted }
                        ]}
                    />
                );
            }
        },
        {
            title: t('wms.sorterPlan.detail.columns.configuredChuteNumber'),
            dataIndex: 'matchesChuteIds',
            key: 'matchesChuteIds',
            width: 120,
            render: (matchesChuteIds: string | null, record: ChuteConfigRecord) => {
                // 从 editableData 中找到当前行的最新数据
                const currentRecord = editableDataMap.get(record.key!);
                
                // 判断是否是配对格口（向上配对）
                const isPaired = !!(currentRecord?.leadChuteId && currentRecord.leadChuteId !== currentRecord.id);
                
                // 如果是配对格口，不显示配对结果（但数据仍然保留）
                if (isPaired) {
                    return <span style={{ color: '#999' }}>--</span>;
                }
                
                // 将matchesChuteIds JSON数组转换为chuteCode数组用于显示
                const selectedValues = parseMatchesChuteIds(currentRecord?.matchesChuteIds);
                
                // 如果没有配对格口，显示空
                if (!selectedValues || selectedValues.length === 0) {
                    return <span style={{ color: '#999' }}>--</span>;
                }
                
                // 显示为逗号分隔的格口号列表
                return <span>{selectedValues.join(', ')}</span>;
            }
        },
        {
            title: t('wms.sorterPlan.detail.columns.chuteType'),
            dataIndex: 'chuteType',
            key: 'chuteType',
            width: 140,
            render: (type: number | null, record: ChuteConfigRecord) => {
                const currentRecord = editableDataMap.get(record.key!);
                // 如果是配对格口（leadChuteId存在且不等于自己的id），则禁用编辑
                const isPaired = !!(currentRecord?.leadChuteId && currentRecord.leadChuteId !== currentRecord.id);
                // 检查是否正在使用中（使用 isUse 判断）
                const isDisabled = isChuteDisabled(record.chuteCode, currentRecord?.isUse);
                
                return (
                    <Select
                        value={type ?? undefined}
                        style={{ width: '100%' }}
                        allowClear
                        placeholder="--"
                        disabled={isPaired || isDisabled}
                        onChange={(value) => {
                            setEditableData(prev => {
                                // 更新当前格口的类型，配对格口固定为禁用类型(3)
                                return prev.map(item => {
                                    if (item.key === record.key) {
                                        // 如果选择异常，清空相关字段（与 applyBusinessTypeChange 清空矩阵对齐），异常类型默认未分配
                                        if (isExceptionChuteType(value)) {
                                            return {
                                                ...item,
                                                chuteType: value,
                                                // 切为异常时默认选中「未分配」；已有选择保留。
                                                // 注意与「删空不回填」并存：这里只给初始值，用户在异常类型列删空后不会再被填回
                                                exceptionCodes: item.exceptionCodes ?? [UNASSIGNED_EXCEPTION_CODE],
                                                businessType: undefined,
                                                carrierCode: undefined,
                                                printType: undefined,
                                                sortingCode: null,
                                                ttBizType: null,
                                                preSortingCode: null,
                                                lastMileSortingCode: null
                                            };
                                        }
                                        // 切到正常/禁用时【不清空】exceptionCodes：保留在本地 state 里，
                                        // 用户来回切类型（异常→正常→异常）时原来选的异常类型还在，不用重选。
                                        // 安全性：非异常格口的该列 render 显示 '--' 不外露；保存出参已按类型过滤
                                        // （handleUpload / handleSaveChutes 里 isExceptionChuteType(...) ? codes : null），非异常格口一律送 null
                                        const isFromException = isExceptionChuteType(item.chuteType);
                                        const isToNormal = value === ChuteType.NORMAL;
                                        if (isFromException && isToNormal) {
                                            // 从异常切正常：补包牌样式默认值 SWX
                                            return { ...item, chuteType: value, printType: PrintType.SWIFTX };
                                        }
                                        return { ...item, chuteType: value };
                                    }
                                    // 如果是当前格口的配对格口，类型固定为禁用(3)
                                    if (item.leadChuteId === record.id && item.leadChuteId !== item.id) {
                                        return { ...item, chuteType: 3 };
                                    }
                                    return item;
                                });
                            });
                        }}
                        options={chuteTypeOptions.map(option => ({
                            value: option.value,
                            label: t(option.labelKey),
                            // 主格口不能选择"禁用"类型（chuteType = 3）
                            disabled: !isPaired && option.value === 3
                        }))}
                    />
                );
            }
        },
        {
            title: t('wms.sorterPlan.detail.columns.exceptionCodes'),
            dataIndex: 'exceptionCodes',
            key: 'exceptionCodes',
            // 正常行只显示 --，异常行的标签是合并 8 列平铺的，本列只要放得下表头即可
            width: 80,
            onCell: (record: ChuteConfigRecord) =>
                isExceptionRow(record) ? { colSpan: EXCEPTION_ROW_COL_SPAN } : {},
            render: (_: string[] | null, record: ChuteConfigRecord) => {
                const currentRecord = editableDataMap.get(record.key!);
                const isPaired = !!(currentRecord?.leadChuteId && currentRecord.leadChuteId !== currentRecord.id);
                const isDisabled = isChuteDisabled(record.chuteCode, currentRecord?.isUse);
                // 仅异常格口可配置异常类型
                if (!isExceptionChuteType(currentRecord?.chuteType ?? null)) {
                    return <span style={{ color: '#999' }}>--</span>;
                }
                return (
                    <Select
                        mode="multiple"
                        value={currentRecord?.exceptionCodes ?? []}
                        // maxWidth 取被合并的 8 列宽度之和：表格是 scroll x=max-content 的自动布局，不封顶的话
                        // 标签一行排开的固有宽度会把整表撑宽，多出来的宽度摊到被合并的列上，「异常类型」列会被撑到近 200px
                        style={{ width: '100%', maxWidth: EXCEPTION_ROW_MAX_WIDTH }}
                        // 单元格已横向合并占满整行，标签全部平铺、按行宽自动换行，故不设 maxTagCount 折叠。
                        // 注意：禁止改成 maxTagCount="responsive"——它在 scroll x=max-content 的表格里会反复测量导致列宽振荡（右侧列闪动）
                        tagRender={({ label, closable, onClose }) => (
                            <Tag
                                closable={closable}
                                onClose={onClose}
                                title={typeof label === 'string' ? label : undefined}
                                style={{ marginInlineEnd: 4, marginBlock: 2, whiteSpace: 'nowrap' }}
                            >
                                {label}
                            </Tag>
                        )}
                        disabled={isPaired || isDisabled}
                        onChange={(values: string[]) => {
                            // 编辑期允许清空，不回填默认值；「异常格口必须选异常类型」由保存时校验拦截
                            setEditableData(prev => prev.map(item =>
                                item.key === record.key ? { ...item, exceptionCodes: values.length ? values : null } : item
                            ));
                        }}
                        options={exceptionCodeOptions.map(option => ({
                            value: option.value,
                            label: t(option.labelKey),
                        }))}
                    />
                );
            }
        },
        {
            title: t('wms.sorterPlan.detail.columns.businessType'),
            dataIndex: 'businessType',
            key: 'businessType',
            onCell: mergedAwayCell,
            width: 140,
            render: (businessType: number | null, record: ChuteConfigRecord) => {
                const currentRecord = editableDataMap.get(record.key!);
                const isPaired = !!(currentRecord?.leadChuteId && currentRecord.leadChuteId !== currentRecord.id);
                const isChuteUsed = isChuteDisabled(record.chuteCode, currentRecord?.isUse);
                
                // 如果格口类型是业务异常(1)或非业务异常(2)，则置灰
                const chuteType = currentRecord?.chuteType ?? record.chuteType;
                const isExceptionChute = isExceptionChuteType(chuteType);
                const isDisabled = isPaired || isChuteUsed || isExceptionChute;

                return (
                    <Select
                        value={businessType ?? undefined}
                        style={{ width: '100%' }}
                        allowClear
                        placeholder="--"
                        disabled={isDisabled}
                        onChange={(value) => {
                            setEditableData(prev => {
                                // 切换包牌码类型时，清空分拣规则/预分拣/TT业务类型/尾程业务段码，避免旧格式残留；
                                // SwiftX / TEMU 固定 SWX 样式，TT 默认 TT 样式（满足前置条件后可手动改 SWX）
                                const applyBusinessTypeChange = (item: ChuteConfigRecord): ChuteConfigRecord => {
                                    let newPrintType = item.printType;
                                    if (value === BusinessType.SWIFTX || value === BusinessType.TEMU) {
                                        newPrintType = PrintType.SWIFTX;
                                    } else if (value === BusinessType.TT) {
                                        newPrintType = PrintType.TT;
                                    }
                                    return {
                                        ...item,
                                        businessType: value,
                                        printType: newPrintType,
                                        sortingCode: null,
                                        ttBizType: null,
                                        preSortingCode: null,
                                        lastMileSortingCode: null
                                    };
                                };
                                return prev.map(item => {
                                    if (item.key === record.key) {
                                        return applyBusinessTypeChange(item);
                                    }
                                    if (item.leadChuteId === record.id && item.leadChuteId !== item.id) {
                                        return applyBusinessTypeChange(item);
                                    }
                                    return item;
                                });
                            });
                        }}
                        options={businessTypeOptions.map(option => ({
                            value: option.value,
                            label: option.label
                        }))}
                    />
                );
            }
        },
        {
            title: t('wms.sorterPlan.detail.columns.ttBusinessType'),
            dataIndex: 'ttBizType',
            key: 'ttBizType',
            onCell: mergedAwayCell,
            width: 110,
            render: (ttBizType: string | null, record: ChuteConfigRecord) => {
                const currentRecord = editableDataMap.get(record.key!);
                const isPaired = !!(currentRecord?.leadChuteId && currentRecord.leadChuteId !== currentRecord.id);
                const isChuteUsed = isChuteDisabled(record.chuteCode, currentRecord?.isUse);
                const chuteType = currentRecord?.chuteType ?? record.chuteType;
                const isExceptionChute = isExceptionChuteType(chuteType);
                // TT业务类型仅包牌码类型=TT 时可选
                const isTt = (currentRecord?.businessType ?? record.businessType) === BusinessType.TT;
                const isDisabled = isPaired || isChuteUsed || isExceptionChute || !isTt;

                return (
                    <Select
                        // 只有非 TT 行才无值可显；使用中/配对/异常格口仅禁编辑，值仍需回显
                        // （格口被分拣机拉过配置后 isUse=true，用 isDisabled 判空会让已配的值凭空消失）
                        value={isTt ? (currentRecord?.ttBizType ?? TtBizType.UNLIMITED) : undefined}
                        style={{ width: '100%' }}
                        placeholder="--"
                        disabled={isDisabled}
                        onChange={(value: string) => {
                            // 值与后端枚举名一致（含 UNLIMITED=不限），直接提交存库；
                            // 预分拣候选随业务类型变化，已选值一并清空
                            setEditableData(prev => prev.map(item => {
                                if (item.key === record.key) return { ...item, ttBizType: value, preSortingCode: null };
                                if (item.leadChuteId === record.id && item.leadChuteId !== item.id) return { ...item, ttBizType: value, preSortingCode: null };
                                return item;
                            }));
                        }}
                        options={ttBizTypeOptions.map(option => ({
                            value: option.value,
                            label: t(option.labelKey)
                        }))}
                    />
                );
            }
        },
        {
            title: t('wms.sorterPlan.detail.columns.tailChute'),
            dataIndex: 'carrierCode',
            key: 'carrierCode',
            onCell: mergedAwayCell,
            width: 180,
            render: (carrierCode: number | null, record: ChuteConfigRecord) => {
                const currentRecord = editableDataMap.get(record.key!);
                const isPaired = !!(currentRecord?.leadChuteId && currentRecord.leadChuteId !== currentRecord.id);
                const isChuteUsed = isChuteDisabled(record.chuteCode, currentRecord?.isUse);
                
                // 如果格口类型是业务异常(1)或非业务异常(2)，则置灰
                const chuteType = currentRecord?.chuteType ?? record.chuteType;
                const isExceptionChute = isExceptionChuteType(chuteType);
                const isDisabled = isPaired || isChuteUsed || isExceptionChute;
                
                return (
                    <Select
                        value={carrierCode}
                        style={{ width: '100%' }}
                        allowClear
                        placeholder="--"
                        disabled={isDisabled}
                        onChange={(value) => {
                            setEditableData(prev => {
                                // 更新当前格口及其所有配对格口的尾程商；
                                // TT 时包牌样式重置为 TT（SWX 需满足前置条件后手动选择），尾程业务段码随之清空
                                const applyCarrierChange = (item: ChuteConfigRecord): ChuteConfigRecord => {
                                    let newPrintType = item.printType;
                                    let newLastMileSortingCode = item.lastMileSortingCode;
                                    if (item.businessType === BusinessType.TT) {
                                        newPrintType = PrintType.TT;
                                        newLastMileSortingCode = null;
                                    }

                                    // 如果是 TT 三段式，需要重新构建分拣规则（因为第三段是承运商代码）
                                    let newSortingCode = item.sortingCode;
                                    if (shouldUseTtThreeSegmentCode(item.businessType, value)) {
                                        const middleSegment = extractMiddleSegment(item.sortingCode);
                                        newSortingCode = buildTtSortingCode(middleSegment, value);
                                    }
                                    // TEMU 的分段候选随尾程商变（USPS 分段 / DHLe 单段），换尾程商后清空重选
                                    if (item.businessType === BusinessType.TEMU) {
                                        newSortingCode = null;
                                    }

                                    return {
                                        ...item,
                                        carrierCode: value,
                                        printType: newPrintType,
                                        sortingCode: newSortingCode,
                                        // 预分拣候选随尾程商变化，已选值一并清空
                                        preSortingCode: null,
                                        lastMileSortingCode: newLastMileSortingCode
                                    };
                                };
                                return prev.map(item => {
                                    if (item.key === record.key) {
                                        return applyCarrierChange(item);
                                    }
                                    // 如果是当前格口的配对格口，也同步更新
                                    if (item.leadChuteId === record.id && item.leadChuteId !== item.id) {
                                        return applyCarrierChange(item);
                                    }
                                    return item;
                                });
                            });
                        }}
                        options={carrierSelectOptions.map(option => {
                            // SwiftX（businessType=1）不能选择 MIX（-1）；TEMU 只能选 USPS / DHLe
                            // 使用 record 而不是 currentRecord 来确保获取到最新的 businessType
                            const businessType = currentRecord?.businessType ?? record.businessType;
                            const shouldDisable = (businessType === BusinessType.SWIFTX && option.value === -1) ||
                                (businessType === BusinessType.TEMU && !TEMU_ALLOWED_CARRIER_CODES.includes(option.value));
                            // 使用缩写显示
                            const abbreviatedLabel = getFinalMileCarrierName(option.value, carrierSelectOptions, true);
                            return {
                                value: option.value,
                                label: abbreviatedLabel,
                                disabled: shouldDisable
                            };
                        })}
                        loading={carrierOptionsLoading}
                    />
                );
            }
        },
        {
            title: t('wms.sorterPlan.detail.columns.sortingRule'),
            dataIndex: 'sortingCode',
            key: 'sortingCode',
            onCell: mergedAwayCell,
            width: 260,
            render: (text: string | null, record: ChuteConfigRecord) => {
                const currentRecord = editableDataMap.get(record.key!);
                const isPaired = !!(currentRecord?.leadChuteId && currentRecord.leadChuteId !== currentRecord.id);
                const isChuteUsed = isChuteDisabled(record.chuteCode, currentRecord?.isUse);

                // 如果格口类型是业务异常(1)或非业务异常(2)，则置灰
                const chuteType = currentRecord?.chuteType ?? record.chuteType;
                const isExceptionChute = isExceptionChuteType(chuteType);
                const isDisabled = isPaired || isChuteUsed || isExceptionChute;

                // 分拣规则仅 TT 可填（SwiftX 的四段码移至"尾程业务段码"列）
                const useTtThreeSegment = shouldUseTtThreeSegmentCode(
                    currentRecord?.businessType,
                    currentRecord?.carrierCode
                );

                // TT 三段式，显示完整的三段码，但只能编辑第二段
                if (useTtThreeSegment) {
                    const middleSegment = extractMiddleSegment(text);
                    const isMixCarrier = currentRecord?.carrierCode === -1;
                    const carrierCodeVal = currentRecord?.carrierCode;
                    const hasCarrier = !isMixCarrier && carrierCodeVal !== undefined && carrierCodeVal !== null;
                    // 该尾程商在 tt_location_config 下的服务子节点（有则第三段用下拉选择）
                    const services = hasCarrier ? (carrierServiceMap.get(carrierCodeVal as number) ?? []) : [];
                    const carrierAbbr = hasCarrier
                        ? getFinalMileCarrierName(carrierCodeVal as number, carrierSelectOptions, true)
                        : '';
                    // 当前第三段：三段码里已存的值，缺省回退缩写（=「不区分」）
                    const currentThirdSegment = extractThirdSegment(text) || carrierAbbr;
                    // 有服务子节点：第三段渲染为下拉（「不区分」值为缩写；各服务值为服务 code）
                    const serviceAddonAfter = (
                        <Select
                            value={currentThirdSegment}
                            disabled={isDisabled}
                            variant="borderless"
                            style={{ minWidth: 96 }}
                            options={[
                                // 「不区分」默认项显示尾程商缩写（如 USM），而非文案「不区分」
                                { value: carrierAbbr, label: `-${carrierAbbr}` },
                                ...services.map(s => ({ value: s.code, label: `-${s.code}` })),
                            ]}
                            onChange={(seg: string) => {
                                setEditableData(prev => prev.map(item => {
                                    if (item.key === record.key || (item.leadChuteId === record.id && item.leadChuteId !== item.id)) {
                                        // 第二段(下一站)填不填都保留所选第三段：直接按三段拼(中段允许为空)，
                                        // 不走 buildTtSortingCode 的「中段为空即返回空串」分支，避免服务码被吞、下拉不回显
                                        const segs = (item.sortingCode || '').split('-');
                                        const middle = segs.length >= 2 ? segs[1] : '';
                                        const from = segs[0] || defaultTtFromCode || '';
                                        const newCode = from ? `${from}-${middle}-${seg}` : item.sortingCode;
                                        return { ...item, sortingCode: newCode };
                                    }
                                    return item;
                                }));
                            }}
                        />
                    );

                    return (
                        <SortingCodeInput
                            initialValue={middleSegment}
                            placeholder={t('wms.sorterPlan.detail.placeholders.enterSecondSegment') as string}
                            disabled={isDisabled}
                            addonBefore={defaultTtFromCode ? `${defaultTtFromCode}-` : undefined}
                            addonAfter={!hasCarrier
                                ? undefined
                                : services.length > 0
                                    ? serviceAddonAfter
                                    : `-${carrierAbbr}`
                            }
                            onCommit={(newMiddleSegment) => {
                                // 保留已选第三段（尾程商服务码/「不区分」缩写），仅重拼第二段
                                const newFullCode = buildTtSortingCode(newMiddleSegment, currentRecord?.carrierCode, extractThirdSegment(text));
                                setEditableData(prev => prev.map(item => {
                                    if (item.key === record.key || (item.leadChuteId === record.id && item.leadChuteId !== item.id)) {
                                        // 分拣规则变化后不再满足本地形态时，包牌样式退回 TT 并清空尾程业务段码
                                        const swxNoLongerAllowed = item.printType === PrintType.SWIFTX &&
                                            item.businessType === BusinessType.TT &&
                                            !isLocalSwxSortingRule(newFullCode);
                                        // 第二段（下一站）变了则原预分拣码不再属于该流向，一并清空
                                        const nextStopChanged =
                                            extractMiddleSegment(item.sortingCode) !== extractMiddleSegment(newFullCode);
                                        return {
                                            ...item,
                                            sortingCode: newFullCode,
                                            printType: swxNoLongerAllowed ? PrintType.TT : item.printType,
                                            lastMileSortingCode: swxNoLongerAllowed ? null : item.lastMileSortingCode,
                                            preSortingCode: nextStopChanged ? null : item.preSortingCode
                                        };
                                    }
                                    return item;
                                }));
                            }}
                        />
                    );
                }

                // TEMU：分段下拉（USPS 三/四段、DHLe 单段），凑齐完整规则才写回 sortingCode；配对格口同步
                if (currentRecord?.businessType === BusinessType.TEMU) {
                    return (
                        <TemuSortingCodeSelect
                            value={currentRecord?.sortingCode ?? text}
                            carrierCode={currentRecord?.carrierCode}
                            disabled={isDisabled}
                            options={temuSortingCodeOptions}
                            onChange={(newCode) => {
                                setEditableData(prev => prev.map(item => {
                                    if (item.key === record.key || (item.leadChuteId === record.id && item.leadChuteId !== item.id)) {
                                        return { ...item, sortingCode: newCode };
                                    }
                                    return item;
                                }));
                            }}
                        />
                    );
                }

                // 非 TT / TEMU（SwiftX/未选择）：分拣规则不可填
                return (
                    <SortingCodeInput
                        initialValue={''}
                        placeholder={'--'}
                        disabled={true}
                        onCommit={() => undefined}
                    />
                );
            }
        },
        {
            title: t('wms.sorterPlan.detail.columns.printType'),
            dataIndex: 'printType',
            key: 'printType',
            onCell: mergedAwayCell,
            width: 110,
            render: (printType: number | null | undefined, record: ChuteConfigRecord) => {
                const currentRecord = editableDataMap.get(record.key!);
                const isPaired = !!(currentRecord?.leadChuteId && currentRecord.leadChuteId !== currentRecord.id);
                const isChuteUsed = isChuteDisabled(record.chuteCode, currentRecord?.isUse);

                // 获取格口类型：如果是配对格口，检查主格口的类型
                let chuteType = currentRecord?.chuteType ?? record.chuteType;
                if (isPaired && currentRecord?.leadChuteId) {
                    const leadChute = editableData.find(item => item.id === currentRecord.leadChuteId);
                    if (leadChute) {
                        chuteType = leadChute.chuteType;
                    }
                }

                // 如果格口类型是业务异常(1)或非业务异常(2)，则置灰且显示为 --
                const isExceptionChute = isExceptionChuteType(chuteType);

                // 仅当 包牌码类型=TT 且 尾程商=SWIFTX(0) 且 分拣规则第一二段相同 时才可编辑（可选 TT/SWX）；
                // 其他情况 TT 固定 TT 样式、SwiftX 固定 SWX 样式
                const businessType = currentRecord?.businessType ?? record.businessType;
                const carrierCode = currentRecord?.carrierCode ?? record.carrierCode;
                const canEditPrintType = businessType === BusinessType.TT && carrierCode === 0 &&
                    isLocalSwxSortingRule(currentRecord?.sortingCode ?? record.sortingCode);
                const isDisabled = isPaired || isChuteUsed || isExceptionChute || !canEditPrintType;

                return (
                    <Select
                        value={isExceptionChute ? undefined : (printType ?? undefined)}
                        style={{ width: '100%' }}
                        placeholder="--"
                        disabled={isDisabled}
                        onChange={(value) => {
                            setEditableData(prev => {
                                // 包牌样式从 SWX 切回 TT 时清空尾程业务段码（仅 SWX 样式可填）；
                                // 切到 SWX 时清空预分拣码（SWX 本地交接包不选预分拣，预分拣列随之置灰）
                                const applyPrintTypeChange = (item: ChuteConfigRecord): ChuteConfigRecord => {
                                    let newSortingCode = item.sortingCode;
                                    if (shouldUseTtThreeSegmentCode(item.businessType, item.carrierCode)) {
                                        const middleSegment = extractMiddleSegment(item.sortingCode);
                                        // 保留已选第三段（尾程商服务码/「不区分」缩写）
                                        newSortingCode = buildTtSortingCode(middleSegment, item.carrierCode, extractThirdSegment(item.sortingCode));
                                    }
                                    const newLastMileSortingCode = value === PrintType.TT ? null : item.lastMileSortingCode;
                                    const newPreSortingCode = value === PrintType.SWIFTX ? null : item.preSortingCode;
                                    return { ...item, printType: value, sortingCode: newSortingCode, lastMileSortingCode: newLastMileSortingCode, preSortingCode: newPreSortingCode };
                                };
                                return prev.map(item => {
                                    if (item.key === record.key) {
                                        return applyPrintTypeChange(item);
                                    }
                                    if (item.leadChuteId === record.id && item.leadChuteId !== item.id) {
                                        return applyPrintTypeChange(item);
                                    }
                                    return item;
                                });
                            });
                        }}
                        options={printTypeOptions.map(option => ({
                            value: option.value,
                            label: option.label
                        }))}
                    />
                );
            }
        },
        {
            title: t('wms.sorterPlan.detail.columns.preSortCode'),
            dataIndex: 'preSortingCode',
            key: 'preSortingCode',
            onCell: mergedAwayCell,
            width: 150,
            render: (preSortingCode: string | null, record: ChuteConfigRecord) => {
                const currentRecord = editableDataMap.get(record.key!);
                const isPaired = !!(currentRecord?.leadChuteId && currentRecord.leadChuteId !== currentRecord.id);
                const isChuteUsed = isChuteDisabled(record.chuteCode, currentRecord?.isUse);
                const chuteType = currentRecord?.chuteType ?? record.chuteType;
                const isExceptionChute = isExceptionChuteType(chuteType);
                // 预分拣仅 TT 可选；包牌样式=SWX（本地交接包）不选预分拣，切到 SWX 时值已同步清空
                const isTt = (currentRecord?.businessType ?? record.businessType) === BusinessType.TT;
                const isSwxPrint = (currentRecord?.printType ?? record.printType) === PrintType.SWIFTX;
                const isDisabled = isPaired || isChuteUsed || isExceptionChute || !isTt || isSwxPrint;

                // 候选来自 fromCode×尾程商 的预取缓存（行数据就绪即预取），
                // 下一站(分拣规则第二段) 在此本地过滤；TT业务类型不参与（后端已废弃该维度）
                const carrierCode = currentRecord?.carrierCode ?? null;
                const preSortNextStop = extractMiddleSegment(currentRecord?.sortingCode ?? record.sortingCode);
                const candidates = carrierCode != null
                    ? filterPreSortCandidates(carrierCode, preSortNextStop)
                    : [];
                return (
                    <Select
                        // 同 TT业务类型列：非 TT 行才无值，其余 disabled 原因只禁编辑不影响回显
                        value={isTt ? (currentRecord?.preSortingCode ?? '') : undefined}
                        style={{ width: '100%' }}
                        placeholder="--"
                        disabled={isDisabled}
                        onDropdownVisibleChange={(open) => {
                            // 兜底：预取失败（网络抖动等）时展开重试；正常路径缓存已热，这里是 no-op
                            if (open && carrierCode != null) {
                                prefetchPreSortOptions(carrierCode);
                            }
                        }}
                        onChange={(value: string) => {
                            const newPreSortingCode = value || null;
                            setEditableData(prev => prev.map(item => {
                                if (item.key === record.key) return { ...item, preSortingCode: newPreSortingCode };
                                if (item.leadChuteId === record.id && item.leadChuteId !== item.id) return { ...item, preSortingCode: newPreSortingCode };
                                return item;
                            }));
                        }}
                        options={[
                            { value: '', label: t('wms.sorterPlan.detail.preSort.none') },
                            ...candidates.map(code => ({ value: code, label: code }))
                        ]}
                    />
                );
            }
        },
        {
            title: t('wms.sorterPlan.detail.columns.lastMileSortingCode'),
            dataIndex: 'lastMileSortingCode',
            key: 'lastMileSortingCode',
            onCell: mergedAwayCell,
            width: 220,
            render: (text: string | null, record: ChuteConfigRecord) => {
                const currentRecord = editableDataMap.get(record.key!);
                const isPaired = !!(currentRecord?.leadChuteId && currentRecord.leadChuteId !== currentRecord.id);
                const isChuteUsed = isChuteDisabled(record.chuteCode, currentRecord?.isUse);
                const chuteType = currentRecord?.chuteType ?? record.chuteType;
                const isExceptionChute = isExceptionChuteType(chuteType);
                // SwiftX 自由填写；TT 仅包牌样式=SWX 时可填
                const businessType = currentRecord?.businessType ?? record.businessType;
                const printType = currentRecord?.printType ?? record.printType;
                const canEdit = businessType === BusinessType.SWIFTX ||
                    (businessType === BusinessType.TT && printType === PrintType.SWIFTX);
                const isDisabled = isPaired || isChuteUsed || isExceptionChute || !canEdit;

                return (
                    <SortingCodeInput
                        initialValue={currentRecord?.lastMileSortingCode || ''}
                        placeholder={isDisabled ? '--' : (t('wms.sorterPlan.detail.placeholders.sortingCode') as string)}
                        disabled={isDisabled}
                        onCommit={(newValue) => {
                            setEditableData(prev => prev.map(item => {
                                if (item.key === record.key) return { ...item, lastMileSortingCode: newValue || null };
                                if (item.leadChuteId === record.id && item.leadChuteId !== item.id) return { ...item, lastMileSortingCode: newValue || null };
                                return item;
                            }));
                        }}
                    />
                );
            }
        },
    ];

    // 处理改为主格口
    const handleChangeToLeadChute = (currentIndex: number) => {
        setEditableData(prev => {
            const updatedData = [...prev];
            const currentChute = updatedData[currentIndex];
            
            if (!currentChute) return prev;
            
            // 记录原来的主格口ID（用于更新原主格口的配对列表）
            const oldLeadChuteId = currentChute.leadChuteId;
            
            // 找到原主格口，继承其格口类型
            let inheritedChuteType = currentChute.chuteType;
            if (oldLeadChuteId && oldLeadChuteId !== currentChute.id) {
                const oldLeadChute = updatedData.find(chute => chute.id === oldLeadChuteId);
                if (oldLeadChute) {
                    inheritedChuteType = oldLeadChute.chuteType;
                }
            }
            
            // 1. 将当前格口改为主格口（leadChuteId设为自己的id），并继承原主格口的类型
            currentChute.leadChuteId = currentChute.id;
            currentChute.chuteType = inheritedChuteType;
            currentChute.matchesChuteIds = currentChute.id ? JSON.stringify([currentChute.id]) : null;
            
            // 2. 找到当前格口下方直接相邻的"向上配对"格口，将它们与当前格口配对
            const pairedChutes: number[] = currentChute.id ? [currentChute.id] : [];
            for (let i = currentIndex + 1; i < updatedData.length; i++) {
                const nextChute = updatedData[i];
                // 如果下一个格口原本是向上配对的，且leadChuteId不等于自己的id
                if (nextChute.leadChuteId && nextChute.leadChuteId !== nextChute.id) {
                    // 将它配对到当前格口
                    nextChute.leadChuteId = currentChute.id;
                    if (currentChute.id && nextChute.id) {
                        pairedChutes.push(nextChute.id);
                    }
                    // 配对格口的类型固定为禁用（3），同步主格口的业务字段
                    nextChute.chuteType = 3; // 配对格口固定为禁用类型
                    nextChute.carrierCode = currentChute.carrierCode;
                    nextChute.sortingCode = currentChute.sortingCode;
                    nextChute.businessType = currentChute.businessType;
                    nextChute.printType = currentChute.printType;
                    nextChute.ttBizType = currentChute.ttBizType;
                    nextChute.preSortingCode = currentChute.preSortingCode;
                    nextChute.lastMileSortingCode = currentChute.lastMileSortingCode;
                } else {
                    // 遇到主格口就停止
                    break;
                }
            }
            
            // 3. 更新当前格口及其配对格口的matchesChuteIds
            if (pairedChutes.length > 0 && currentChute.id) {
                const matchesChuteIds = JSON.stringify(pairedChutes.sort((a, b) => a - b));
                updatedData.forEach(chute => {
                    if (chute.leadChuteId === currentChute.id) {
                        chute.matchesChuteIds = matchesChuteIds;
                    }
                });
            }
            
            // 4. 如果原来是配对格口，需要从原主格口的配对列表中移除（在重新配对下方格口之后）
            if (oldLeadChuteId && oldLeadChuteId !== currentChute.id && currentChute.id) {
                // 重新计算原主格口的配对列表（包含原主格口本身 + 仍然配对到原主格口的格口）
                const oldLeadPairedChutes: number[] = [];
                updatedData.forEach(chute => {
                    // 包含主格口自己
                    if (chute.id === oldLeadChuteId) {
                        oldLeadPairedChutes.push(chute.id);
                    }
                    // 包含仍然配对到原主格口的格口
                    else if (chute.leadChuteId === oldLeadChuteId && chute.id) {
                        oldLeadPairedChutes.push(chute.id);
                    }
                });
                
                const oldMatchesChuteIds = JSON.stringify(oldLeadPairedChutes.sort((a, b) => a - b));
                
                updatedData.forEach(chute => {
                    if (chute.id === oldLeadChuteId || chute.leadChuteId === oldLeadChuteId) {
                        chute.matchesChuteIds = oldMatchesChuteIds;
                    }
                });
            }
            
            return updatedData;
        });
    };

    // 处理改为向上配对
    const handleChangeToPairUpward = (currentIndex: number) => {
        setEditableData(prev => {
            const updatedData = [...prev];
            const currentChute = updatedData[currentIndex];
            
            if (!currentChute) return prev;
            
            // 获取当前格口的前缀（格口组）
            const currentPrefix = currentChute.chuteCode?.match(/^[A-Za-z]+/)?.[0] || '';
            
            // 1. 向上查找最近的同组主格口
            let newLeadChuteId: number | undefined = undefined;
            let newLeadChute: ChuteConfigRecord | undefined = undefined;
            
            for (let i = currentIndex - 1; i >= 0; i--) {
                const prevChute = updatedData[i];
                const prevPrefix = prevChute.chuteCode?.match(/^[A-Za-z]+/)?.[0] || '';
                
                // 只查找同组的格口
                if (prevPrefix !== currentPrefix) {
                    continue;
                }
                
                // 找到主格口（leadChuteId等于自己的id或为null）
                if (!prevChute.leadChuteId || prevChute.leadChuteId === prevChute.id) {
                    newLeadChuteId = prevChute.id;
                    newLeadChute = prevChute;
                    break;
                }
            }
            
            if (!newLeadChuteId || !newLeadChute) {
                message.warning(t('wms.sorterPlan.detail.messages.noLeadChuteFound'));
                return prev;
            }
            
            // 2. 记录原本配对到当前格口的所有格口（如果当前格口原来是主格口）
            const oldLeadChuteId = currentChute.id;
            const wasLeadChute = !currentChute.leadChuteId || currentChute.leadChuteId === currentChute.id;
            const childChutes: number[] = [];

            if (wasLeadChute && oldLeadChuteId) {
                // 找出所有配对到当前格口的子格口
                updatedData.forEach((chute, index) => {
                    if (index !== currentIndex &&
                        chute.leadChuteId === oldLeadChuteId &&
                        chute.id !== oldLeadChuteId) {
                        childChutes.push(index);
                    }
                });
            }
            
            // 3. 将当前格口配对到找到的新主格口
            currentChute.leadChuteId = newLeadChuteId;

            // 4. 配对格口的类型固定为禁用（3），同步主格口的业务字段
            currentChute.chuteType = 3; // 配对格口固定为禁用类型
            currentChute.carrierCode = newLeadChute.carrierCode;
            currentChute.sortingCode = newLeadChute.sortingCode;
            currentChute.businessType = newLeadChute.businessType;
            currentChute.printType = newLeadChute.printType;
            currentChute.ttBizType = newLeadChute.ttBizType;
            currentChute.preSortingCode = newLeadChute.preSortingCode;
            currentChute.lastMileSortingCode = newLeadChute.lastMileSortingCode;
            
            // 5. 将原本配对到当前格口的子格口也配对到新主格口
            childChutes.forEach(childIndex => {
                const childChute = updatedData[childIndex];
                childChute.leadChuteId = newLeadChuteId;
                // 配对格口的类型固定为禁用（3），同步主格口的业务字段
                if (newLeadChute) {
                    childChute.chuteType = 3; // 配对格口固定为禁用类型
                    childChute.carrierCode = newLeadChute.carrierCode;
                    childChute.sortingCode = newLeadChute.sortingCode;
                    childChute.businessType = newLeadChute.businessType;
                    childChute.printType = newLeadChute.printType;
                    childChute.ttBizType = newLeadChute.ttBizType;
                    childChute.preSortingCode = newLeadChute.preSortingCode;
                    childChute.lastMileSortingCode = newLeadChute.lastMileSortingCode;
                }
            });

            // 6. 更新新主格口的配对列表（包含主格口自己和所有配对到它的格口）
            const pairedChutes: number[] = [];
            updatedData.forEach(chute => {
                // 包含主格口自己
                if (chute.id === newLeadChuteId && chute.id) {
                    pairedChutes.push(chute.id);
                }
                // 包含所有配对到主格口的格口
                else if (chute.leadChuteId === newLeadChuteId && chute.id) {
                    pairedChutes.push(chute.id);
                }
            });

            const matchesChuteIds = JSON.stringify(pairedChutes.sort((a, b) => a - b));
            // 更新主格口和所有配对格口的 matchesChuteIds
            updatedData.forEach(chute => {
                if (chute.id === newLeadChuteId || chute.leadChuteId === newLeadChuteId) {
                    chute.matchesChuteIds = matchesChuteIds;
                }
            });
            
            return updatedData;
        });
    };

    // 下载模板
    const handleDownloadTemplate = () => {
        if (!planDetail?.sorterId) {
            message.error('无法获取分拣机信息');
            return;
        }
        downloadTemplate({
            variables: {
                sorterId: planDetail.sorterId,
                language: i18n.language
            }
        });
    };

    // 处理文件上传
    const handleUpload = async (file: File) => {
        setUploading(true);
        try {
            // 1. 先上传文件到对象存储
            const uploadResponse = await fileUpload(file);

            if (!uploadResponse.objectKey) {
                throw new Error(t('wms.sorterPlan.detail.messages.fileUploadFailed') as string);
            }

            // 2. 调用GraphQL导入接口
            const result = await importChutes({
                variables: {
                    sorterPlanId: parseInt(id!),
                    objectKey: uploadResponse.objectKey,
                    language: i18n.language
                }
            });

            const importResponse = result.data?.importSorterPlanChutes;
            
            if (importResponse) {
                setUploadModalVisible(false); // 关闭上传弹窗
                setCurrentTab('chutes'); // 切换到格口配置标签页

                // 如果有失败记录，显示失败弹窗，不更新表格数据
                if (importResponse.failedCount > 0) {
                    const failedData = importResponse.failedRecords.map((record: ImportFailedRecord, index: number) => ({
                        ...record,
                        rowNumber: index + 1
                    }));
                    setFailedRecords(failedData);
                    setFailedModalVisible(true);
                    setImportResult(importResponse);
                    return;
                }

                // 只有全部成功时才更新表格数据（直接覆盖）
                if (importResponse.successRecords && importResponse.successRecords.length > 0) {
                    // 数据格式与详情页加载的数据格式保持完全一致
                    const initialData = importResponse.successRecords.map((record: SorterPlanChuteRecord, index: number) => ({
                        key: `config-${index}`, // 使用与详情页完全相同的key格式
                        id: record.id,
                        chuteCode: record.chuteCode || '',
                        leadChuteId: record.leadChuteId,
                        matchesChuteIds: record.matchesChuteIds || null,
                        chuteType: record.chuteType ?? null,
                        // 异常类型来自 Excel 解析结果（空列已在后端解析时归一为未分配）
                        exceptionCodes: isExceptionChuteType(record.chuteType) ? (record.exceptionCodes ?? null) : null,
                        carrierCode: record.carrierCode,
                        sortingCode: record.sortingCode || null,
                        businessType: record.businessType,
                        printType: record.printType ?? PrintType.SWIFTX,
                        ttBizType: record.ttBizType ?? null,
                        preSortingCode: record.preSortingCode ?? null,
                        lastMileSortingCode: parseSortingCodeForDisplay(record.lastMileSortingCode)
                    }));
                    setEditableData(initialData);
                    message.success(importResponse.message);
                }
            }
        } catch (error: any) {
            const errorMessage = error?.message || t('wms.sorterPlan.detail.messages.importFailed');
            message.error(errorMessage);
            console.error('Upload error:', error);
        } finally {
            setUploading(false);
        }
    };

    // 导出到文件
    const handleExport = async () => {
        try {
            setExporting(true);
            const result = await exportChutes({
                variables: {
                    sorterPlanId: parseInt(id!),
                    language: i18n.language
                }
            });

            const downloadUrl = result.data?.exportSorterPlanChutes;
            if (downloadUrl) {
                // 直接使用返回的下载URL下载文件
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = `sorter_plan_chute_export_${id}.xlsx`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                message.success(t('wms.sorterPlan.detail.messages.exportSuccess'));
            } else {
                message.error(t('wms.sorterPlan.detail.messages.exportFailed'));
            }
        } catch (error) {
            console.error('Export error:', error);
            message.error(t('wms.sorterPlan.detail.messages.exportFailed'));
        } finally {
            setExporting(false);
        }
    };

    // 二次确认保存并选中
    const showSaveAndUseConfirm = () => {
        // 获取设备类型，判断是否为交叉带（sorterType = 2）
        const sorterType = data?.getSorterPlanDetail?.sorterType;
        const isCrossBelt = sorterType === 2;
        
        Modal.confirm({
            title: t('wms.sorterPlan.detail.buttons.saveAndUse'),
            content: isCrossBelt 
                ? t('wms.sorterPlan.detail.messages.saveAndUseConfirmCrossBelt')
                : t('wms.sorterPlan.detail.messages.saveAndUseConfirm'),
            okText: t('common.confirm'),
            cancelText: t('common.cancel'),
            centered: true,
            onOk: () => handleSaveChutes(2),
        });
    };

    // 格式化分拣码：如果是四段格式（LAX-DW-A-001），将最后两段合并为 LAX-DW-A001
    const formatSortingCode = (sortingCode: string | null | undefined): string | null => {
        if (!sortingCode || !sortingCode.trim()) {
            return null;
        }
        
        const trimmed = sortingCode.trim();
        const segments = trimmed.split('-');
        
        // 只有四段格式才需要处理
        if (segments.length === 4) {
            // 合并最后两段：LAX-DW-A-001 -> LAX-DW-A001
            const result = `${segments[0]}-${segments[1]}-${segments[2]}${segments[3]}`;
            return result;
        }
        return trimmed;
    };

    // 保存格口配置（数据格式和后端完全一致，无需转换）
    const handleSaveChutes = async (operateType: number) => {
        if (!id || !editableData || editableData.length === 0) {
            return;
        }

        setSaving(true);
        try {
            const chutes = editableData.map((record) => {
                // 确保 matchesChuteIds 是正确的JSON格式 [1,2,3]，而不是 ["1","2","3"]
                let matchesChuteIds = record.matchesChuteIds;
                if (matchesChuteIds) {
                    try {
                        // 尝试解析JSON
                        const parsed = JSON.parse(matchesChuteIds);
                        // 如果是数组，确保所有元素都是数字
                        if (Array.isArray(parsed)) {
                            const numArray = parsed.map(item => typeof item === 'number' ? item : parseInt(item)).filter(n => !isNaN(n));
                            matchesChuteIds = JSON.stringify(numArray);
                        }
                    } catch (e) {
                        console.error('解析 matchesChuteIds 失败:', e);
                    }
                }
                
                // TT 行分拣规则兜底重拼：始发地代码异步加载完成前提交过中段时，
                // sortingCode 可能只存了裸中段（如 "ds"），保存前统一按 前缀-中段-承运商缩写 重拼
                let sortingCodeToSave = record.sortingCode;
                if (record.businessType === BusinessType.TT && defaultTtFromCode &&
                    record.carrierCode !== null && record.carrierCode !== undefined) {
                    // 保留已选第三段（尾程商服务码/「不区分」缩写），仅兜底重拼前缀与中段
                    sortingCodeToSave = buildTtSortingCode(extractMiddleSegment(record.sortingCode), record.carrierCode, extractThirdSegment(record.sortingCode));
                }
                // SwiftX 行分拣规则禁填：存量残值（改造前旧 sorting_code）在 UI 上不可见也无法清除，
                // 提交时强制置空，避免整单保存被后端校验拒绝
                if (record.businessType === BusinessType.SWIFTX) {
                    sortingCodeToSave = null;
                }

                // 格式化分拣码；TEMU 四段（SCF-H-LA-970）不能走"合并末两段"的 SwiftX 口径，原样提交
                const formattedSortingCode = record.businessType === BusinessType.TEMU
                    ? (sortingCodeToSave?.trim() || null)
                    : formatSortingCode(sortingCodeToSave);
                
                return {
                    id: record.id,
                    sorterPlanId: parseInt(id),
                    chuteCode: record.chuteCode,
                    leadChuteId: record.leadChuteId,
                    matchesChuteIds: matchesChuteIds,
                    chuteType: record.chuteType,
                    exceptionCodes: isExceptionChuteType(record.chuteType)
                        ? (record.exceptionCodes?.length ? record.exceptionCodes : null)
                        : null,
                    carrierCode: record.carrierCode,
                    sortingCode: formattedSortingCode,
                    businessType: record.businessType,
                    printType: record.printType ?? PrintType.SWIFTX,
                    // UNLIMITED(0) 仅前端表示"不限"，提交转 null
                    ttBizType: record.ttBizType ? record.ttBizType : null,
                    preSortingCode: record.preSortingCode || null,
                    lastMileSortingCode: formatSortingCode(record.lastMileSortingCode) || null,
                };
            });

            const result = await batchSaveChutes({
                variables: {
                    sorterPlanId: parseInt(id),
                    chutes: chutes,
                    operateType: operateType,
                    isDropBag: isDropBagEnabled,
                    printCount: printCount,
                    // 始终上送当前值：清空时显式传 null（不传后端会保持原值）
                    ppmodBagContainerId: ppmodBagContainerId,
                },
            });

            if (result.data?.batchSaveSorterPlanChutes) {
                message.success(
                    operateType === 1
                        ? t('wms.sorterPlan.detail.messages.saveSuccess')
                        : t('wms.sorterPlan.detail.messages.saveAndUseSuccess')
                );
                // 仅保存(operateType=1)时停留在当前页，保存并选中(operateType=2)时跳转回列表页
                if (operateType === 2) {
                    navigate('/wms/sorterPlan');
                } else {
                    // 仅保存时刷新数据
                    refetch();
                    
                    // 滚动到页面顶部
                    // 使用 setTimeout 确保在数据更新后再滚动
                    setTimeout(() => {
                        // 滚动 Table 内部的滚动容器到顶部
                        const tableBody = document.querySelector('.ant-table-body');
                        if (tableBody) {
                            tableBody.scrollTop = 0;
                        }
                    }, 100);
                }
            } else {
                message.error(t('wms.sorterPlan.detail.messages.saveFailed'));
            }

        } catch (error: any) {
            console.error('Save error:', error);
            
            // 解析 GraphQL 错误，优先使用 messageKey
            let errorMessage = t('wms.sorterPlan.detail.messages.saveFailed');
            
            if (error?.graphQLErrors?.[0]?.extensions?.messageKey) {
                const messageKey = error.graphQLErrors[0].extensions.messageKey;
                const details = error.graphQLErrors[0].extensions.details;
                // 传入 details 以支持占位符替换（如 {{code1}}, {{code2}}）
                errorMessage = String(t(messageKey, details)) || messageKey;
            } else if (error?.graphQLErrors?.[0]?.message) {
                const backendMessage = error.graphQLErrors[0].message;
                // 如果后端消息以 errors. 开头，尝试作为翻译键
                if (backendMessage.startsWith('errors.')) {
                    errorMessage = t(backendMessage) || backendMessage;
                } else {
                    errorMessage = backendMessage;
                }
            } else if (error?.message) {
                errorMessage = error.message;
            }
            
            message.error(errorMessage as string);
        } finally {
            setSaving(false);
        }
    };

    // 取消导入
    const handleCancelImport = () => {
        // 跳转回列表页
        navigate('/wms/sorterPlan');
    };

    // 开始编辑方案名称
    const handleStartEditPlanName = () => {
        setEditingPlanName(true);
        setPlanNameValue(planDetail?.sorterPlanName || '');
    };

    // 保存方案名称
    const handleSavePlanName = async () => {
        if (!planNameValue.trim()) {
            message.error(t('wms.sorterPlan.validation.sorterPlanNameRequired'));
            return;
        }

        try {
            await updateSorterPlan({
                variables: {
                    input: {
                        id: parseInt(id!),
                        sorterId: planDetail?.sorterId,
                        sorterPlanName: planNameValue.trim(),
                        status: planDetail?.status,
                        version: planDetail?.version,
                        isDropBag: planDetail?.isDropBag,
                        isSpecified: planDetail?.isSpecified,
                        printCount: planDetail?.printCount ?? 1
                    }
                }
            });
        } catch (error) {
            console.error('Update plan name error:', error);
        }
    };

    // 取消编辑方案名称
    const handleCancelEditPlanName = () => {
        setEditingPlanName(false);
        setPlanNameValue(planDetail?.sorterPlanName || '');
    };

    const tabItems = [
        {
            key: 'chutes',
            label: t('wms.sorterPlan.detail.tabs.chutes'),
        },
        {
            key: 'flow',
            label: t('wms.sorterPlan.detail.tabs.flow'),
        },
    ];

    return (
        <div className="flex flex-col h-screen">
            <div className="flex-1 overflow-hidden flex flex-col p-6">
                {/* 头部标题行 */}
                <div className="mb-6 flex items-center gap-3">
                    <div className="w-1 h-8 bg-purple-600"></div>
                    <div className="flex items-center gap-3">
                        {editingPlanName ? (
                            <div className="flex items-center gap-2">
                                <Input
                                    value={planNameValue}
                                    onChange={(e) => setPlanNameValue(e.target.value)}
                                    onPressEnter={handleSavePlanName}
                                    style={{width: 300}}
                                    autoFocus
                                />
                                <Button
                                    type="primary"
                                    size="small"
                                    icon={<CheckOutlined />}
                                    onClick={handleSavePlanName}
                                />
                                <Button
                                    size="small"
                                    icon={<CloseOutlined />}
                                    onClick={handleCancelEditPlanName}
                                />
                            </div>
                        ) : (
                            <>
                                <h2 className="text-2xl font-semibold m-0">
                                    {planDetail?.sorterName && planDetail?.sorterPlanName 
                                        ? `${planDetail.sorterName} — ${planDetail.sorterPlanName}`
                                        : planDetail?.sorterPlanName || planDetail?.sorterName || '--'}
                                </h2>
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<EditOutlined />}
                                    onClick={handleStartEditPlanName}
                                />
                            </>
                        )}
                        {planDetail?.status === 1 ? (
                            <Tag color="green" className="text-sm">
                                {t('wms.sorterPlan.status.inUse')}
                            </Tag>
                        ) : (
                            <Tag color="default" className="text-sm">
                                {t('wms.sorterPlan.status.unused')}
                            </Tag>
                        )}
                    </div>
                </div>

                {/* 落格组包开关 - 放在 Tabs 上方 */}
                <div className="mb-4 flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">
                        {t('wms.sorterPlan.columns.isDropBag')}:
                    </span>
                    <Switch
                        checked={isDropBagEnabled}
                        onChange={setIsDropBagEnabled}
                        checkedChildren={t('wms.sorterPlan.isDropBag.enabled')}
                        unCheckedChildren={t('wms.sorterPlan.isDropBag.disabled')}
                    />
                    <span className="ml-6 text-sm font-medium text-gray-700">
                        {t('wms.sorterPlan.columns.printCount')}:
                    </span>
                    <InputNumber
                        min={1}
                        max={10}
                        precision={0}
                        value={printCount}
                        onChange={(value) => setPrintCount(value ?? 1)}
                    />
                    {showPpmodContainer && (
                        <>
                            <span className="ml-6 text-sm font-medium text-gray-700">
                                {t('wms.sorterPlan.columns.ppmodBagContainer')}:
                            </span>
                            <Select
                                allowClear
                                className="w-56"
                                placeholder={t('wms.sorterPlan.columns.ppmodBagContainerPlaceholder')}
                                value={ppmodBagContainerId ?? undefined}
                                onChange={(value?: number) => setPpmodBagContainerId(value ?? null)}
                                options={ppmodContainerOptions}
                            />
                        </>
                    )}
                    <span className="ml-6 text-sm font-medium text-gray-700">
                        {t('wms.sorterPlan.detail.warehouse')}:
                    </span>
                    <span className="text-sm">{workWarehouseName || planDetail?.warehouseId || '--'}</span>
                </div>

                <Tabs
                    activeKey={currentTab}
                    onChange={setCurrentTab}
                    items={tabItems}
                    className="sorterPlanTabs"
                    tabBarExtraContent={{
                        right: (
                            <div className="flex gap-3">
                    <Button
                        type="default"
                        icon={<FileTextOutlined />}
                        onClick={handleDownloadTemplate}
                        loading={downloadingTemplate}
                        className="font-semibold"
                    >
                        {t('wms.sorterPlan.detail.buttons.downloadTemplate')}
                    </Button>

                    <Button
                        type="primary"
                        icon={<UploadOutlined />}
                        onClick={() => setUploadModalVisible(true)}
                        className="font-semibold"
                    >
                        {t('wms.sorterPlan.detail.buttons.importFromFile')}
                    </Button>

                    <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={handleExport}
                        loading={exporting}
                        className="font-semibold"
                    >
                        {t('wms.sorterPlan.detail.buttons.exportToFile')}
                    </Button>
                </div>
                        )
                    }}
                />

                {/* 根据当前 tab 显示不同内容 */}
                {currentTab === 'chutes' && (
                    <>
                        <div className="flex-1 overflow-hidden">
                            {/* 格口配置表格 */}
                            <Table<ChuteConfigRecord>
                                columns={columns}
                                dataSource={editableData}
                                rowKey={(record, index) => record.key || `row-${index}`}
                                loading={loading}
                                pagination={false}
                                scroll={{x: 'max-content', y: 'calc(100vh - 300px)'}}
                                sticky
                                size="small"
                                rowClassName={(record) => {
                                    // 如果当前格口在边角格口列表中或正在使用中，返回置灰样式类名
                                    return isChuteDisabled(record.chuteCode, record.isUse) ? 'bg-gray-100 opacity-60' : '';
                                }}
                            />
                        </div>

                        {/* 底部按钮区域 */}
                        <div className="mt-2 border-t border-gray-200 pt-2 flex justify-between items-center bg-white flex-shrink-0">
                        <Button
                            type="primary"
                            onClick={showSaveAndUseConfirm}
                            loading={saving}
                            disabled={editableData.length === 0}
                        >
                            {t('wms.sorterPlan.detail.buttons.saveAndUse')}
                        </Button>
                        <div className="flex gap-3">
                            <Button
                                onClick={() => handleSaveChutes(1)}
                                loading={saving}
                                disabled={editableData.length === 0 || planDetail?.status === 1}
                            >
                                {t('wms.sorterPlan.detail.buttons.saveOnly')}
                            </Button>
                            <Button
                                onClick={handleCancelImport}
                                disabled={saving}
                            >
                            {t('common.cancel')}
                        </Button>
                        </div>
                        </div>
                    </>
                )}

                {currentTab === 'flow' && (
                    <div className="flex-1 overflow-hidden">
                        {/* 流向统计视图 */}
                        <FlowView data={flowData} loading={flowLoading} />
                    </div>
                )}
            </div>

            {/* 文件上传弹窗 */}
            <Modal
                title={t('wms.sorterPlan.detail.buttons.importFromFile')}
                open={uploadModalVisible}
                onCancel={() => {
                    if (!uploading) {
                        setUploadModalVisible(false);
                    }
                }}
                footer={[
                    <Button
                        key="cancel"
                        onClick={() => {
                            if (!uploading) {
                                setUploadModalVisible(false);
                            }
                        }}
                        disabled={uploading}
                    >
                        {t('common.cancel')}
                    </Button>
                ]}
                width={800}
                closable={!uploading}
                maskClosable={!uploading}
            >
                <Dragger
                    accept=".xlsx,.xls"
                    showUploadList={false}
                    beforeUpload={(file: File) => {
                        // 验证文件类型
                        const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                            file.type === 'application/vnd.ms-excel' ||
                            file.name.toLowerCase().endsWith('.xlsx') ||
                            file.name.toLowerCase().endsWith('.xls');

                        if (!isExcel) {
                            message.error(t('wms.sorterPlan.detail.messages.onlyExcelAllowed'));
                            return false;
                        }

                        // 验证文件大小（限制为 10MB）
                        const isLt10M = file.size / 1024 / 1024 < 10;
                        if (!isLt10M) {
                            message.error(t('wms.sorterPlan.detail.messages.fileSizeLimit'));
                            return false;
                        }

                        handleUpload(file);
                        return false;
                    }}
                    disabled={uploading}
                >
                    <p className="ant-upload-drag-icon">
                        <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">
                        {uploading ? t('wms.sorterPlan.detail.upload.uploading') || '文件上传中...' : t('wms.sorterPlan.detail.upload.dragOrClick')}
                    </p>
                    <p className="ant-upload-hint">
                        {t('wms.sorterPlan.detail.upload.supportFormat')}
                    </p>
                </Dragger>
            </Modal>

            {/* 导入失败弹窗 */}
            <Modal
                title={
                    <div className="flex items-center gap-2">
                        <span className="text-red-500 text-xl">⚠</span>
                        <span>{t('wms.sorterPlan.detail.import.failed')}</span>
                    </div>
                }
                open={failedModalVisible}
                onCancel={() => {
                    setFailedModalVisible(false);
                    setFailedRecords([]);
                    setImportResult(null);
                }}
                footer={[
                    <Button
                        key="close"
                        type="primary"
                        onClick={() => {
                            setFailedModalVisible(false);
                            setFailedRecords([]);
                            setImportResult(null);
                        }}
                    >
                        {t('common.close')}
                    </Button>
                ]}
                width={900}
            >
                {/* 失败提示信息 */}
                {importResult && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded">
                        <p className="text-base text-red-600 m-0">{importResult.message}</p>
                    </div>
                )}

                {/* 失败记录表格 */}
                <Table
                    columns={[
                        {
                            title: t('wms.sorterPlan.detail.import.rowNumber'),
                            dataIndex: 'rowNumber',
                            key: 'rowNumber',
                            width: 80,
                        },
                        {
                            title: t('wms.sorterPlan.detail.import.chuteCode'),
                            dataIndex: 'chuteCode',
                            key: 'chuteCode',
                            width: 120,
                        },
                        {
                            title: t('wms.sorterPlan.detail.import.failureReason'),
                            dataIndex: 'errorMessage',
                            key: 'errorMessage',
                            render: (_: any, record: ImportFailedRecord & { rowNumber: number }) => {
                                return getLocalizedErrorMessage(record);
                            }
                        }
                    ]}
                    dataSource={failedRecords}
                    rowKey={(record) => `failed-${record.chuteCode}-${record.rowNumber}`}
                    pagination={false}
                    scroll={{ y: 400 }}
                />
            </Modal>
        </div>
    );
};

export default SorterPlanDetailPage;

